import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Home, Star, BookOpen, Calendar, Info, Mail, ChevronRight, Send, Phone } from 'lucide-react'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [services, setServices] = useState([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    // Hent ydelser til dropdown og mobilmenu
    fetch('/_api/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const published = data.filter((s) => s.published !== false)
          const getOrderIndex = (service) => {
            const text = `${service.service_slug?.da || ''} ${service.slug?.da || ''} ${service.title?.da || ''}`.toLowerCase()
            if (text.includes('bryllup')) return 0
            if (text.includes('firmafest')) return 1
            if (text.includes('foedselsdag') || text.includes('fødselsdag')) return 2
            if (text.includes('sax')) return 3
            if (text.includes('vinyl')) return 4
            return 99
          }
          const sorted = [...published].sort((a, b) => getOrderIndex(a) - getOrderIndex(b))
          setServices(sorted)
        }
      })
      .catch((err) => console.error('Fejl ved hentning af ydelser til menu:', err))
  }, [])

  // Luk menuen og nulstil mobil-dropdown når ruten ændres
  useEffect(() => {
    setIsOpen(false)
    setIsDropdownOpen(false)
  }, [location])

  // Lås body scroll når menuen er åben
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <header className={`sticky top-0 w-full border-b border-zinc-900 bg-black/90 backdrop-blur-md transition-all ${isOpen ? 'z-[9999]' : 'z-50'}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3 md:py-4">
        {/* Main Header Row */}
        <div className="flex items-center justify-between gap-4">
          {/* Logo & Compact Review Link */}
          <div className="flex flex-col items-start gap-1 shrink-0">
            <Link
              to="/anmeldelser"
              className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-amber-400 font-medium tracking-tight border-b border-zinc-800/80 pb-1 w-full group/link"
            >
              <span className="flex items-center gap-0.5 text-amber-400 shrink-0">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              </span>
              <span className="text-zinc-300 font-semibold text-[11px] sm:text-xs leading-none group-hover/link:text-white group-hover/link:underline transition-colors">Anmeldelser</span>
              <ChevronRight className="h-3.5 w-3.5 text-zinc-400 group-hover/link:text-amber-400 transition-all shrink-0 translate-y-[0.5px] group-hover/link:translate-x-0.5" />
            </Link>
            <Link to="/" className="flex items-center gap-3 group shrink-0">
              <img
                src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/86054219-a7e4-4879-b662-316ddd7c6cb7.png"
                alt="DJ Ron9 Logo"
                width="160"
                height="48"
                fetchPriority="high"
                className="h-8 sm:h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Navigation (Aligned on line with logo) - Now Services */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {services.length > 0 ? (
              services.map((service) => {
                const text = `${service.service_slug?.da || ''} ${service.slug?.da || ''} ${service.title?.da || ''}`.toLowerCase()
                let targetUrl = `/ydelser/${service.service_slug?.da || service.slug?.da || ''}`
                if (text.includes('bryllup')) targetUrl = '/dj-til-bryllup'
                else if (text.includes('firmafest')) targetUrl = '/lej-dj-til-firmafest'
                else if (text.includes('foedselsdag') || text.includes('fødselsdag')) targetUrl = '/dj-til-foedselsdag'
                else if (text.includes('sax')) targetUrl = '/dj-og-saxofon'
                else if (text.includes('vinyl')) targetUrl = '/vinyl-dj'

                const slug = service.service_slug?.da || service.slug?.da
                const isActive = location.pathname === targetUrl || location.pathname === targetUrl + '/' || location.pathname === `/ydelser/${slug}`
                return (
                  <Link
                    key={service.id}
                    to={targetUrl}
                    className={`text-xs font-semibold uppercase tracking-wider transition-all duration-200 hover:text-[#bb9b56] ${
                      isActive ? 'text-[#bb9b56]' : 'text-zinc-400'
                    }`}
                  >
                    {service.title?.da}
                  </Link>
                )
              })
            ) : (
              <span className="text-xs text-zinc-500">Henter ydelser...</span>
            )}
          </nav>

          {/* Desktop & Mobile CTA, Phone & Hamburger */}
          <div className="flex items-center gap-3 sm:gap-6 shrink-0">
            <Link
              to="/kontakt"
              className="hidden xs:inline-flex items-center gap-1.5 rounded-full bg-[#bb9b56] px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-[#bb9b56]/15 transition-all hover:scale-105 hover:bg-[#a88645]"
            >
              <Send className="h-3.5 w-3.5" />
              Få tilbud nu
            </Link>

            {/* Hamburger Button */}
            <button
              onClick={() => setIsOpen(true)}
              className="lg:hidden flex items-center justify-center text-[#bb9b56] bg-transparent border-0 p-0 outline-none focus:outline-none"
              aria-label="Åbn menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Secondary Navbar for Main Pages (Desktop only) */}
      <div className="hidden lg:block border-t border-zinc-900 bg-zinc-950/40 py-2.5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-8 xl:gap-12">
            <Link
              to="/blog"
              className={`text-xs font-semibold uppercase tracking-wider transition-colors hover:text-[#bb9b56] ${
                location.pathname.startsWith('/blog') ? 'text-[#bb9b56]' : 'text-zinc-400'
              }`}
            >
              Blog
            </Link>

            <Link
              to="/om"
              className={`text-xs font-semibold uppercase tracking-wider transition-colors hover:text-[#bb9b56] ${
                location.pathname === '/om' ? 'text-[#bb9b56]' : 'text-zinc-400'
              }`}
            >
              Om DJ Ron9
            </Link>

            <Link
              to="/kontakt"
              className={`text-xs font-semibold uppercase tracking-wider transition-colors hover:text-[#bb9b56] ${
                location.pathname === '/kontakt' ? 'text-[#bb9b56]' : 'text-zinc-400'
              }`}
            >
              Kontakt
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <div className={`fixed inset-0 z-[10000] lg:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        {/* Backdrop overlay */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Sidebar Panel */}
        <div className={`fixed inset-y-0 right-0 w-full max-w-xs h-full min-h-screen bg-zinc-950 border-l border-zinc-900 p-6 shadow-2xl flex flex-col justify-between transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div>
            {/* Sidebar Header with Logo & Close Button */}
            <div className="flex items-center justify-between pb-6 border-b border-zinc-900">
              <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
                <img
                  src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/86054219-a7e4-4879-b662-316ddd7c6cb7.png"
                  alt="DJ Ron9 Logo"
                  width="130"
                  height="40"
                  className="h-10 w-auto object-contain"
                />
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-900 hover:text-[#bb9b56] focus:outline-none"
                aria-label="Luk menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="mt-8 space-y-4">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                  location.pathname === '/' ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                }`}
              >
                Forside
              </Link>

              {/* Services listed directly as top-level menu items */}
              {services.length > 0 ? (
                services.map((service) => {
                  const text = `${service.service_slug?.da || ''} ${service.slug?.da || ''} ${service.title?.da || ''}`.toLowerCase()
                  let targetUrl = `/ydelser/${service.service_slug?.da || service.slug?.da || ''}`
                  if (text.includes('bryllup')) targetUrl = '/dj-til-bryllup'
                  else if (text.includes('firmafest')) targetUrl = '/lej-dj-til-firmafest'
                  else if (text.includes('foedselsdag') || text.includes('fødselsdag')) targetUrl = '/dj-til-foedselsdag'
                  else if (text.includes('sax')) targetUrl = '/dj-og-saxofon'
                  else if (text.includes('vinyl')) targetUrl = '/vinyl-dj'

                  const slug = service.service_slug?.da || service.slug?.da
                  const isActive = location.pathname === targetUrl || location.pathname === targetUrl + '/' || location.pathname === `/ydelser/${slug}`
                  return (
                    <Link
                      key={service.id}
                      to={targetUrl}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                        isActive ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                      }`}
                    >
                      {service.title?.da}
                    </Link>
                  )
                })
              ) : (
                <div className="px-3 py-2 text-xs text-zinc-500">Henter ydelser...</div>
              )}

              <Link
                to="/anmeldelser"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                  location.pathname === '/anmeldelser' ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                }`}
              >
                Anmeldelser
              </Link>

              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                  location.pathname.startsWith('/blog') ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                }`}
              >
                Blog
              </Link>

              <Link
                to="/om"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                  location.pathname === '/om' ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                }`}
              >
                Om DJ Ron9
              </Link>

              <Link
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-zinc-900 hover:text-[#bb9b56] ${
                  location.pathname === '/kontakt' ? 'text-[#bb9b56] bg-zinc-900/50' : 'text-zinc-300'
                }`}
              >
                Kontakt
              </Link>
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="pt-6 border-t border-zinc-900 space-y-4">
            <span
              className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-zinc-300"
            >
              <Phone className="h-4 w-4 text-[#bb9b56]" />
              50 99 82 82
            </span>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-2 w-full text-center rounded-full bg-[#bb9b56] py-3 text-sm font-semibold text-white shadow-lg shadow-[#bb9b56]/15 transition-all hover:bg-[#a88645]"
            >
              <Send className="h-4 w-4" />
              Få tilbud nu
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}
